import { Typography } from '@mui/material';
import moment from 'moment';
import {
  AutocompleteArrayInput,
  ChipField,
  Datagrid,
  DateField,
  Filter,
  NumberField,
  ReferenceArrayField,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  SelectArrayInput,
  SelectInput,
  SingleFieldList,
  TextField,
  useListContext,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import DateTimeInputUTC from '../../components/DateTimeInputUTC';
import { Wrapper } from '../../components';
import { CPFField, FullAdressField, PhoneField } from '../../components/Fields';
import ListPagination from '../../components/ListPagination';
import Permission from '../../components/Permission';
import ReportList from '../../components/ReportList';
import { PERMISSIONS } from '../../constants';
import { formatReal } from "../../lib";
import { TagType } from '../../models';
import { filterToQuery } from '../../utils';
import RequiredChain from '../../components/RequiredChain';

const titleSx = {
  margin: '.5rem 0 .5rem 0',
  textAlign: 'center',
  fontWeight: 'bold'
};

const alignSx = {
  textAlign: 'center',
};

const DrawingList = () => {
  const props = useListContext();

  let data = [];
  let summaryData = [];

  if (props.data) {
    const reportInfo = props.data[0];

    if (reportInfo) {
      data = reportInfo.data;
      summaryData = reportInfo.summaryData;
    }
  }

  return (
    <>
      <Typography sx={titleSx}>Total</Typography>
      <Datagrid bulkActionButtons={false} data={summaryData} style={{ marginBottom: 60 }} total={1}>
        <NumberField textAlign="center" label="Clientes" source="cnt_customer" sortable={false} emptyText="0" />
        <NumberField textAlign="center" label="Números" source="cnt_number" sortable={false} emptyText="0" />
      </Datagrid>
      <Typography sx={titleSx}>Relatório de Sorteios</Typography>
      <Datagrid bulkActionButtons={false} data={data} total={1}>
        <ReferenceField source="customer_id" reference="customers" label="Nome" link={"show"} textAlign='center' sortable={false} emptyText="Não identificado">
          <TextField label="Nome" source="name" />
        </ReferenceField>
        <ReferenceField source="customer_id" reference="customers" label="CPF" link={false} textAlign="center" sortable={false} emptyText="Não identificado">
          <CPFField label="CPF" source="cpf" />
        </ReferenceField>
        <ReferenceField source="customer_id" reference="customers" label="RG" link={false} textAlign="center" sortable={false} emptyText="Não identificado">
          <TextField label="RG" source="identityCard" />
        </ReferenceField>
        <NumberField label="Número da Sorte" source="lucky_number" textAlign="center" />
        <DateField label="Data de Atribuição" source="assigned_at" showTime locales="pt-BR" textAlign="center" sortable={false} emptyText="-" />
        <ReferenceField source="customer_id" reference="customers" label="Telefone" link={false} textAlign="center" sortable={false} emptyText="Não identificado">
          <PhoneField label="Telefone" source="cellphone" />
        </ReferenceField>
        <ReferenceField source="customer_id" reference="customers" label="Endereço" link={false} textAlign="center" sortable={false} emptyText="Não identificado">
          <FullAdressField label="Endereço" />
        </ReferenceField>
      </Datagrid>
    </>
  );
};

const DrawingExporter = async (data, fetchRelatedRecords) => {
  const relations = [{
    field: 'customer_id',
    resource: 'customers',
  }];
  const drawingData = data[0] ? data[0].data : [];

  const fetchData = await fetchRelatedRecords(relations, drawingData);

  const getData = (resource) => {
    return fetchData.find(({ resource: res }) => res === resource)?.results || [];
  }

  const customers = data[0].data.map(drawing => {
    const customer = getData('customers').find(cust => cust.id === drawing.customer_id) || {};
    return {
      nome: customer.name,
      cpf: customer.cpf,
      rg: customer.identity_card,
      numero_da_sorte: drawing.lucky_number,
      data_atribuicao: drawing.assigned_at ? moment(drawing.assigned_at).format('DD/MM/YYYY HH:mm:ss') : '',
      telefone: customer.cellphone,
      endereco: (customer.street ? customer.street : '') +
        (customer.street_number ? `, n° ${customer.street_number}` : '') +
        (customer.complement ? `, ${customer.complement}` : '') +
        (customer.neighbourhood ? ` - ${customer.neighbourhood}` : '') +
        (customer.city ? `, ${customer.city}` : '') +
        (customer.state ? ` - ${customer.state}` : '')
    };
  });

  return customers;
};

const DrawingRuleChip = () => {
  const record = useRecordContext();
  if (!record) { return null };

  const name = `${record.drawingRule.label} - ${record.fuel?.name || 'Todos'} ${formatValueToDrawingRule(record)}`;
  return <ChipField record={{ name }} source="name" />;
};

const FilterTablePrint = () => {
  const { filterValues } = useListContext();

  return (
    <Datagrid
      bulkActionButtons={false}
      data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]}
      total={1}>
      <ReferenceField source="drawingId" textAlign="center" label="Sorteio" reference="chains/chainId/drawings" link={false}>
        <TextField source="name" />
      </ReferenceField>
      <ReferenceArrayField
        label="Regra - Combustível - Valor"
        source="drawingRuleFuelIds"
        reference="drawing-rule-fuels"
        link={false}
        textAlign="center"
      >
        <SingleFieldList linkType={false}>
          <DrawingRuleChip />
        </SingleFieldList>
      </ReferenceArrayField>
      <ReferenceArrayField textAlign="center" label="Postos" source="placeIds" reference="stations" link={false}>
        <SingleFieldList linkType={false}>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
      {
        Permission({
          permission: PERMISSIONS.TAG,
          children: (
            <ReferenceArrayField textAlign="center" label="Etiqueta" source="tagIds" reference="chains/chainId/tag">
              <SingleFieldList linkType={false}>
                <ChipField source="name" />
              </SingleFieldList>
            </ReferenceArrayField>
          )
        })
      }
      <DateField source='generatedReportAt' label='Gerado às' showTime sortable={false} locales='pt-BR' sx={alignSx} />
    </Datagrid>
  );
};

const CustomPagination = () => {
  const { filterValues } = useListContext();
  if (!filterValues.drawingId) {
    return (
      <Wrapper >
        <Typography style={{ textAlign: 'center', fontSize: 20 }}>Selecione um Sorteio</Typography>
      </Wrapper>
    );
  }
  return <ListPagination />;
};

const formatValueToDrawingRule = (drawingRuleFuel) => {
  if (!drawingRuleFuel.value) { return ''; }

  if (drawingRuleFuel.drawingRule && drawingRuleFuel.drawingRule.name === 'fillin-value') {
    return `- ${formatReal(drawingRuleFuel.value)}`;
  }

  return `- ${drawingRuleFuel.value}L`;
};

const FilterList = () => {
  const { filterValues } = useListContext();
  const translate = useTranslate();

  return (
    <Filter>
      <ReferenceInput
        reference="chains/chainId/drawings"
        source="drawingId"
        sort={{ field: "name", order: "ASC" }}
        style={{ minWidth: 260 }}
        perPage={null}
        alwaysOn
      >
        <SelectInput
          label="Sorteio"
          optionText="name"
        />
      </ReferenceInput>
      <DateTimeInputUTC
        source="assignedAtFrom"
        label="Data Atribuição Inicial"
        alwaysOn
      />
      <DateTimeInputUTC
        source="assignedAtTo"
        label="Data Atribuição Final"
        alwaysOn
      />
      {filterValues.drawingId && (
        <ReferenceArrayInput
          source="drawingRuleFuelIds"
          reference="drawing-rule-fuels"
          filter={{ drawingId: filterValues.drawingId }}
          sort={{ field: "startDate", order: "ASC" }}
          perPage={null}
          alwaysOn
        >
          <SelectArrayInput
            label="Regra - Combustível - Valor"
            style={{ minWidth: 260 }}
            optionText={record => `${record.drawingRule.label} - ${record.fuel?.name || 'Todos'} ${formatValueToDrawingRule(record)}`}
            helperText={false}
          />
        </ReferenceArrayInput>
      )}
      <ReferenceArrayInput
        source="placeIds"
        reference="stations"
        style={{ minWidth: 260 }}
        sort={{ field: "name", order: "ASC" }}
        perPage={null}
        alwaysOn
      >
        <AutocompleteArrayInput
          sx={{ minWidth: 260, '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 50 } }}
          filterToQuery={filterToQuery}
          label="Postos"
          optionText="name"
          disabled={!filterValues.drawingId}
          noOptionsText={translate('ra.input.autocomplete.no_options_text')}
        />
      </ReferenceArrayInput>
      {
        Permission({
          permission: PERMISSIONS.TAG,
          children: (
            <ReferenceArrayInput
              source="tagIds"
              reference="chains/chainId/tag"
              sort={{ field: "name", order: "ASC" }}
              filter={{ type: TagType.client }}
              style={{ minWidth: 260 }}
              perPage={null}
              alwaysOn
            >
              <SelectArrayInput
                label="Etiqueta"
                optionText="name"
                disabled={!filterValues.drawingId} />
            </ReferenceArrayInput>
          )
        })
      }
    </Filter>
  );
};

const ReportDrawing = (props) => {
  return (
    <Permission permission={PERMISSIONS.DRAWING_REPORT}>
      <RequiredChain>
        <ReportList periodRequired
          {...props}
          basePath='report-drawing'
          title='Sorteios'
          resource='chains/chainId/reports/drawing'
          sort={{ field: 'lucky_number', order: 'DESC' }}
          perPage={25}
          filters={<FilterList />}
          filterDefaultValues={{
            from: moment().startOf("day").subtract(1, "month").toISOString(),
            to: moment().endOf("day").toISOString(),
          }}
          pagination={<CustomPagination />}
          bulkActionButtons={false}
          customExporter={DrawingExporter}
          titleOnPrint='Relatório de Sorteios'
          filterTablePrint={<FilterTablePrint />}
          landscape
        >
          <DrawingList />
        </ReportList>
      </RequiredChain>
    </Permission >
  );
};

export default ReportDrawing;